import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Link2, Search, X, ArrowRight, Building2, CheckCircle2, Loader2 } from 'lucide-react';
import { EmptyState } from './_shared';
import { LinkSameInvoiceModal } from './LinkSameInvoiceModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===================================================================
export function NewSheetWizard({ token, onClose, onSuccess }) {
  const [items, setItems] = useState([]);
  const [itemsCache, setItemsCache] = useState({}); // key -> item snapshot (يحفظ كل العناصر المُحمّلة)
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [supplier, setSupplier] = useState('');
  const [invoiceInputs, setInvoiceInputs] = useState({}); // key -> { invoice_number, invoice_date, amount, group_id }
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const key = (item) => `${item.delivery_id}-${item.supplier_index}-${item.receipt_index}`;

  const toggleSelect = (k) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (supplier) params.append('supplier', supplier);
        const res = await fetch(`${API_URL}/api/invoice-matching/available-receipts?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          const fetched = data.items || [];
          setItems(fetched);
          // إضافة العناصر المعروضة إلى الـ cache (لا نمسح القديم)
          setItemsCache(prev => {
            const next = { ...prev };
            fetched.forEach(it => { next[key(it)] = it; });
            return next;
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [token, search, supplier]);

  const updateInput = (k, field, value) => {
    setInvoiceInputs(prev => ({
      ...prev,
      [k]: { ...prev[k], [field]: value }
    }));
  };

  const filledKeys = Object.entries(invoiceInputs)
    .filter(([k, v]) => v?.invoice_number?.trim() && itemsCache[k])
    .map(([k]) => k);
  const filledCount = filledKeys.length;

  const handleSave = async () => {
    const payload = [];
    filledKeys.forEach(k => {
      const item = itemsCache[k];
      const inp = invoiceInputs[k];
      if (!item || !inp?.invoice_number?.trim()) return;
      const r = item.receipt || {};
      payload.push({
        receipt_delivery_id: item.delivery_id,
        supplier_index: item.supplier_index,
        receipt_index: item.receipt_index,
        supplier_name: item.supplier_name,
        receipt_number: r.receipt_number,
        receipt_date: r.receipt_date,
        project_number: r.project_number,
        project_name: r.project_name,
        invoice_number: inp.invoice_number.trim(),
        invoice_date: inp.invoice_date || null,
        amount: inp.amount ? parseFloat(inp.amount) : null,
        group_id: inp.group_id || null,
      });
    });

    if (payload.length === 0) {
      toast.error('أدخل رقم فاتورة واحد على الأقل');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/invoice-matching/sheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ items: payload, notes }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`تم حفظ كشف الفواتير #${data.sheet.sheet_number} بـ ${payload.length} فاتورة`);
        onSuccess(data.sheet);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'فشل في الحفظ');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center sm:justify-center sm:p-4" dir="rtl">
      <div className="bg-gray-50 w-full sm:max-w-4xl sm:rounded-2xl flex flex-col h-full sm:h-[92vh] overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10 px-4 py-3 flex items-center gap-3">
          <button onClick={onClose} className="w-10 h-10 -mr-2 rounded-full hover:bg-gray-100 flex items-center justify-center">
            <ArrowRight className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-gray-900 text-base sm:text-lg">إنشاء كشف فواتير جديد</h2>
            <p className="text-xs text-gray-500">يظهر فقط الاستلامات المعتمدة بدون فواتير</p>
          </div>
          <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full">
            {filledCount} / {items.length}
          </span>
        </header>

        {/* Filters */}
        <div className="bg-white border-b px-4 py-3 flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ابحث برقم الاستلام، المورد، المشروع، أو رقم الكشف..."
              data-testid="wizard-search-input"
              className="w-full h-11 border rounded-lg pr-10 pl-4 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              autoFocus
            />
          </div>
          <input
            type="text"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            placeholder="فلتر بالمورد..."
            className="h-11 border rounded-lg px-3 text-sm focus:ring-2 focus:ring-purple-500 outline-none sm:w-56"
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="ممتاز!" subtitle="كل الاستلامات المعتمدة لها فواتير مرتبطة" />
          ) : (
            items.map((item) => {
              const r = item.receipt || {};
              const k = key(item);
              const inp = invoiceInputs[k] || {};
              const filled = !!inp.invoice_number?.trim();
              return (
                <div key={k} className={`border-2 rounded-2xl p-3 ${filled ? 'border-emerald-300 bg-emerald-50/30' : selectedKeys.has(k) ? 'border-purple-400 bg-purple-50/30' : 'border-gray-200 bg-white'}`}>
                  {/* Receipt info */}
                  <div className="flex items-start gap-2 mb-3">
                    <label className="shrink-0 mt-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(k)}
                        onChange={() => toggleSelect(k)}
                        data-testid={`select-receipt-${k}`}
                        className="w-5 h-5 accent-purple-600 cursor-pointer"
                      />
                    </label>
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                      <Building2 className="w-4 h-4 text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-900 truncate">{item.supplier_name}</div>
                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                        <span>استلام #{r.receipt_number}</span>
                        <span>•</span>
                        <span>{r.receipt_date}</span>
                        <span>•</span>
                        <span>كشف #{item.delivery_number}</span>
                        {r.project_number && <><span>•</span><span>مشروع {r.project_number}</span></>}
                      </div>
                      {inp.group_id && (
                        <div className="text-[10px] text-purple-700 mt-0.5 flex items-center gap-1">
                          <Link2 className="w-3 h-3" /> مجموعة فاتورة موحَّدة
                        </div>
                      )}
                    </div>
                    {filled && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />}
                  </div>

                  {/* Inputs */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="رقم الفاتورة *"
                      value={inp.invoice_number || ''}
                      onChange={(e) => updateInput(k, 'invoice_number', e.target.value)}
                      data-testid={`invoice-num-${k}`}
                      className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm font-semibold focus:border-purple-500 outline-none"
                    />
                    <input
                      type="date"
                      placeholder="تاريخ الفاتورة"
                      value={inp.invoice_date || ''}
                      onChange={(e) => updateInput(k, 'invoice_date', e.target.value)}
                      className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm focus:border-purple-500 outline-none"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="المبلغ"
                      value={inp.amount || ''}
                      onChange={(e) => updateInput(k, 'amount', e.target.value)}
                      className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm focus:border-purple-500 outline-none"
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Selection action bar — يظهر فقط عند اختيار استلام أو أكثر */}
        {selectedKeys.size > 0 && (
          <div className="border-t bg-purple-50 px-4 py-2.5 flex items-center gap-2" data-testid="selection-action-bar">
            <span className="text-sm font-semibold text-purple-800">
              {selectedKeys.size} استلام محدَّد
            </span>
            <div className="flex-1" />
            <button
              onClick={clearSelection}
              className="text-xs px-3 py-1.5 rounded-lg text-gray-600 hover:bg-white"
            >
              إلغاء التحديد
            </button>
            <button
              onClick={() => {
                if (selectedKeys.size < 2) {
                  toast.error('اختر استلامَين أو أكثر للربط بنفس الفاتورة');
                  return;
                }
                setLinkModalOpen(true);
              }}
              disabled={selectedKeys.size < 2}
              data-testid="link-same-invoice-btn"
              className="text-sm px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold disabled:opacity-50 flex items-center gap-1.5"
            >
              <Link2 className="w-4 h-4" />
              ربط بنفس الفاتورة
            </button>
          </div>
        )}

        {/* Footer */}
        <footer className="border-t bg-white px-4 py-3 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="ملاحظات على الكشف (اختياري)"
            className="flex-1 h-12 px-3 border-2 border-gray-200 rounded-xl text-sm focus:border-purple-500 outline-none"
          />
          <button
            onClick={handleSave}
            disabled={saving || filledCount === 0}
            data-testid="save-matching-sheet-btn"
            className="min-h-[52px] bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white font-bold rounded-xl px-6 disabled:opacity-50 flex items-center justify-center gap-2 text-base"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            حفظ الكشف ({filledCount})
          </button>
        </footer>
      </div>

      {/* Modal: ربط بنفس الفاتورة */}
      {linkModalOpen && (
        <LinkSameInvoiceModal
          count={selectedKeys.size}
          onClose={() => setLinkModalOpen(false)}
          onApply={({ invoice_number, invoice_date, amount }) => {
            const group_id = `grp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const keysArr = Array.from(selectedKeys);
            setInvoiceInputs(prev => {
              const next = { ...prev };
              keysArr.forEach((k, idx) => {
                next[k] = {
                  ...(next[k] || {}),
                  invoice_number,
                  invoice_date: invoice_date || '',
                  // المبلغ يُسجَّل على أول استلام فقط حتى لا يتضاعف الإجمالي
                  amount: idx === 0 ? (amount || '') : '',
                  group_id,
                };
              });
              return next;
            });
            setLinkModalOpen(false);
            clearSelection();
            toast.success(`تم ربط ${keysArr.length} استلامات بالفاتورة #${invoice_number}`);
          }}
        />
      )}
    </div>
  );
}

// ===================================================================
// 3) Print Sheet View — PDF-ready full screen
